import { Slot } from '@radix-ui/react-slot';
import {
  type ComponentPropsWithoutRef,
  cloneElement,
  type ElementRef,
  forwardRef,
  isValidElement,
  type ReactNode,
} from 'react';
import { composeVariants, cva, cx } from '../../utils/cva';
import { jsxTruthy } from '../../utils/jsx-truthy';
import { useFocusVisible } from '../../utils/use-focus-visible';
import { DummyCheckbox } from '../Checkbox';
import { dataFocusRing } from '../FocusVisible';
import { RadioGroupDummyItem } from '../RadioGroup';

export type MenuListItemFontWeight = 'normal' | 'semibold';

export interface MenuListProps extends ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
  elevated?: boolean;
  itemFontWeight?: MenuListItemFontWeight;
  'data-testid'?: string;
}

export const MenuList = forwardRef<ElementRef<'div'>, MenuListProps>(
  (props, forwardedRef) => {
    const {
      className,
      asChild,
      elevated,
      itemFontWeight = 'semibold',
      'data-testid': dataTestId = 'menu-list',
      ...rest
    } = props;

    const Component = asChild ? Slot : 'div';

    return (
      <Component
        ref={forwardedRef}
        className={cx(
          'border-subtle',
          'border',
          'rounded-lg',
          'overflow-hidden',
          'bg-default',
          itemFontWeight === 'normal'
            ? '[--menu-list-item-font-weight:400]'
            : '[--menu-list-item-font-weight:600]',
          elevated && 'shadow-lg',
          className,
        )}
        role="menu"
        tabIndex={-1}
        data-testid={dataTestId}
        {...rest}
      />
    );
  },
);

const menuItemVariants = composeVariants(
  dataFocusRing,
  cva({
    base: [
      'group/radio',
      'group/checkbox',
      'relative',
      'text-default',
      'bg-default',
      '[&:hover:not([data-disabled])]:bg-default-hover',
      '[&[data-highlighted]:not(:hover):not([data-disabled])]:bg-default-active',
      '[&:active:not([data-disabled])]:bg-default-active',
      'text-sm',
      'font-[var(--menu-list-item-font-weight)]',
      'relative',
      'rounded-sm',
      'flex',
      'items-center',
      'gap-3',
      'px-3',
      'mx-2',
      'first:mt-2',
      'last:mb-2',
      'min-h-9',
      'cursor-pointer',
      'select-none',
      '[--icon-size:1.4em]',
      'disabled:pointer-events-none',
      'disabled:text-state-disabled',
      'disabled:cursor-default',
      'data-[disabled]:pointer-events-none',
      'data-[disabled]:text-state-disabled',
      'data-[disabled]:cursor-not-allowed',
      'data-[focus-visible]:z-[1]',
      'data-[state=checked]:bg-default',
      'data-[state=checked]:hover:bg-default-active',
      'data-[state=checked]:data-[highlighted]:[&:not(:hover)]:bg-default-hover',
      'data-[state=checked]:active:bg-default-active',
    ],
    variants: {
      fontWeight: {
        normal: 'font-normal',
        semibold: 'font-semibold',
      },
    },
  }),
);

const menuLabelClassName = cx('flex-1', 'truncate');

export interface MenuListItemProps extends ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
  appendStart?: ReactNode;
  appendEnd?: ReactNode;
  fontWeight?: MenuListItemFontWeight;
  'data-testid'?: string;
}

export const MenuListItem = forwardRef<ElementRef<'div'>, MenuListItemProps>(
  (props, forwardedRef) => {
    const {
      className,
      appendStart,
      appendEnd,
      children,
      onFocus,
      onBlur,
      asChild,
      fontWeight,
      'data-testid': dataTestId = 'menu-list-item',
      ...rest
    } = props;

    const Component = asChild ? Slot : 'div';

    const appendStartElement = jsxTruthy(appendStart) ? (
      <span data-append-start="" data-testid={`${dataTestId}-start`}>
        {appendStart}
      </span>
    ) : null;

    const appendEndElement = jsxTruthy(appendEnd) ? (
      <span data-append-end="" data-testid={`${dataTestId}-end`}>
        {appendEnd}
      </span>
    ) : null;

    return (
      <Component
        ref={forwardedRef}
        className={menuItemVariants({ className, fontWeight })}
        {...useFocusVisible({ onFocus, onBlur })}
        role="menuitem"
        tabIndex={0}
        data-testid={dataTestId}
        {...rest}
      >
        {asChild && isValidElement(children) ? (
          cloneElement(
            children,
            undefined,
            appendStartElement,
            <span
              data-label
              className={menuLabelClassName}
              data-testid={`${dataTestId}-label`}
            >
              {(children.props as { children: ReactNode }).children}
            </span>,
            appendEndElement,
          )
        ) : (
          <>
            {appendStartElement}
            {jsxTruthy(children) ? (
              <span
                data-label
                className={menuLabelClassName}
                data-testid={`${dataTestId}-label`}
              >
                {children}
              </span>
            ) : null}
            {appendEndElement}
          </>
        )}
      </Component>
    );
  },
);

export interface MenuListLabelProps extends ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
  'data-testid'?: string;
}

export const MenuListLabel = forwardRef<ElementRef<'div'>, MenuListLabelProps>(
  (props, ref) => {
    const {
      className,
      asChild,
      'data-testid': dataTestId = 'menu-list-label',
      ...rest
    } = props;

    const Component = asChild ? Slot : 'div';

    return (
      <Component
        ref={ref}
        className={cx(
          'text-xs',
          'h-9',
          'text-muted',
          'relative',
          'flex',
          'cursor-default',
          'select-none',
          'items-center',
          'px-3',
          'mx-2',
          'first:mt-2',
          'last:mb-2',
          'font-semibold',
          'truncate',
          className,
        )}
        data-testid={dataTestId}
        {...rest}
      />
    );
  },
);

export interface MenuListSeparatorProps
  extends ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
  'data-testid'?: string;
}

export const MenuListSeparator = forwardRef<
  ElementRef<'div'>,
  MenuListSeparatorProps
>((props, ref) => {
  const {
    className,
    asChild,
    'data-testid': dataTestId = 'menu-list-separator',
    ...rest
  } = props;

  const Component = asChild ? Slot : 'div';

  return (
    <Component
      ref={ref}
      className={cx('h-px', 'mt-2', 'mb-2', 'bg-border-subtle', className)}
      data-testid={dataTestId}
      {...rest}
    />
  );
});

export interface MenuListIconIndicatorProps
  extends ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
  'data-testid'?: string;
}

export const MenuListIconIndicator = forwardRef<
  ElementRef<'div'>,
  MenuListIconIndicatorProps
>((props, ref) => {
  const {
    className,
    asChild,
    'data-testid': dataTestId = 'menu-list-icon-indicator',
    ...rest
  } = props;

  const Component = asChild ? Slot : 'div';

  return (
    <Component
      ref={ref}
      className={cx(
        'invisible',
        'data-[state=checked]:visible',
        '[[data-state=checked]_&]:visible',
        'text-default',
        className,
      )}
      data-testid={dataTestId}
      {...rest}
    />
  );
});

export interface MenuListCheckboxIndicatorProps
  extends ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
  'data-testid'?: string;
}

export const MenuListCheckboxIndicator = forwardRef<
  ElementRef<'div'>,
  MenuListCheckboxIndicatorProps
>((props, ref) => {
  const {
    asChild,
    'data-testid': dataTestId = 'menu-list-checkbox-indicator',
    ...rest
  } = props;

  const Component = asChild ? Slot : 'div';

  return (
    <Component ref={ref} data-testid={dataTestId} {...rest}>
      <DummyCheckbox className="pointer-events-none">
        <span />
      </DummyCheckbox>
    </Component>
  );
});

export interface MenuListRadioIndicatorProps
  extends ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
  'data-testid'?: string;
}

export const MenuListRadioIndicator = forwardRef<
  ElementRef<'div'>,
  MenuListRadioIndicatorProps
>((props, ref) => {
  const {
    asChild,
    'data-testid': dataTestId = 'menu-list-radio-indicator',
    ...rest
  } = props;

  const Component = asChild ? Slot : 'div';

  return (
    <Component ref={ref} data-testid={dataTestId} {...rest}>
      <RadioGroupDummyItem className="pointer-events-none" />
    </Component>
  );
});
